import "@/App.css";
import React, { useState } from "react";
import axios from "axios";
import { Phone, ShieldCheck, MessageCircle, Instagram, Wifi, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// === EDIT THESE TWO LINKS ===
const WHATSAPP_COMMUNITY_URL = "https://chat.whatsapp.com/Ddn25XRV9Y6A2pWlso4iEG";
const INSTAGRAM_URL = "https://instagram.com/thecinevibeindia";
// ============================

const Step = ({ n, color, icon: Icon, title, desc, children }) => (
  <div className="rounded-2xl border border-[#3a2a14]/70 bg-gradient-to-b from-[#140e07] to-[#0a0604] p-5 sm:p-6 flex flex-col sm:flex-row gap-5 items-start">
    <div className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-[#1a0f06] font-bold text-lg`} style={{background: color}}>
      {n}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-3 mb-1">
        <Icon className="w-6 h-6 text-[#f4d089]" />
        <h3 className="text-lg sm:text-xl font-semibold text-[#f4d089] tracking-wide uppercase">{title}</h3>
      </div>
      <p className="text-[#cbbf9f] text-sm mb-4">{desc}</p>
      {children}
    </div>
  </div>
);

export default function FreeWifi() {
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [waClicked, setWaClicked] = useState(false);
  const [igClicked, setIgClicked] = useState(false);
  const [err, setErr] = useState("");

  const phoneValid = /^[6-9]\d{9}$/.test(phone);
  const canSubmit = phoneValid && consent && !leadId && !submitting;
  const allDone = leadId && waClicked && igClicked;

  const register = async () => {
    setErr("");
    if (!phoneValid) return setErr("Enter a valid 10-digit Indian mobile number.");
    if (!consent) return setErr("Please accept the consent to continue.");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/wifi/register`, {
        phone, country_code: "+91", consent: true,
        user_agent: navigator.userAgent,
      });
      setLeadId(data.lead_id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Try again.");
    } finally { setSubmitting(false); }
  };

  const trackClick = async (channel, url) => {
    try { await axios.post(`${API}/wifi/click`, { lead_id: leadId, channel }); } catch {}
    if (channel === "whatsapp") setWaClicked(true);
    if (channel === "instagram") setIgClicked(true);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const connect = async () => {
    try { await axios.post(`${API}/wifi/click`, { lead_id: leadId, channel: "connected" }); } catch {}
    // If you have a captive-portal redirect URL, change the line below:
    window.location.href = "https://www.google.com";
  };

  return (
    <div className="min-h-screen bg-[#070401] text-[#f4d089]">
      <div className="max-w-[860px] mx-auto px-5 sm:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
  Welcome to <span className="text-[#f4d089]">The </span><span className="text-white">Cine</span><span className="text-[#56b6ff]">Vibe</span>
</h1>
          <p className="text-[#cbbf9f] mt-2 text-sm sm:text-base">Lights. Camera. Chill.</p>
          <div className="mt-6 text-sm tracking-[0.3em] text-[#cbbf9f]">FOLLOW THESE STEPS TO UNLOCK</div>
          <div className="text-2xl sm:text-3xl font-bold text-[#f4d089]">FREE Wi-Fi</div>
        </div>

        {err && (
          <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 text-red-200 px-4 py-3 text-sm" data-testid="wifi-error">
            {err}
          </div>
        )}

        <div className="space-y-4">
          {/* STEP 1 - PHONE */}
          <Step n="1" color="#f4d089" icon={Phone}
            title="Enter Your Mobile Number"
            desc="We'll save this so you can enjoy Wi-Fi & updates from CineVibe.">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center gap-2 bg-white/95 text-[#1a0f06] rounded-md px-3 py-3 sm:w-32 font-semibold">
                🇮🇳 +91
              </div>
              <input
                data-testid="wifi-phone-input"
                inputMode="numeric" maxLength={10}
                value={phone}
                disabled={!!leadId}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g,"").slice(0,10))}
                placeholder="Enter Mobile Number"
                className="flex-1 rounded-md px-4 py-3 bg-white/95 text-[#1a0f06] placeholder-[#7a6a4a] outline-none disabled:opacity-60"
              />
            </div>
          </Step>

          {/* STEP 2 - CONSENT */}
          <Step n="2" color="#34d399" icon={ShieldCheck}
            title="Give Your Consent"
            desc="Stay updated with everything happening at CineVibe.">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                data-testid="wifi-consent-checkbox"
                type="checkbox" checked={consent}
                disabled={!!leadId}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 w-5 h-5 accent-emerald-400"
              />
              <span className="text-sm text-[#cbbf9f]">
                I agree to receive updates, offers, events, screenings and promotional messages
                from CineVibe via WhatsApp, SMS and Email.
              </span>
            </label>

            {!leadId && (
              <button
                data-testid="wifi-submit-btn"
                onClick={register}
                disabled={!canSubmit}
                className="mt-4 w-full sm:w-auto px-6 py-3 rounded-md font-semibold bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] disabled:opacity-50"
              >
                {submitting ? "Saving..." : "Continue"}
              </button>
            )}
            {leadId && (
              <div className="mt-4 inline-flex items-center gap-2 text-emerald-300 text-sm">
                <CheckCircle2 className="w-5 h-5" /> Saved successfully
              </div>
            )}
          </Step>

          {/* STEP 3 - WHATSAPP */}
          <Step n="3" color="#a78bfa" icon={MessageCircle}
            title="Join Our WhatsApp Community"
            desc="Be the first to know about screenings, events & offers.">
            <button
              data-testid="wifi-whatsapp-btn"
              disabled={!leadId}
              onClick={() => trackClick("whatsapp", WHATSAPP_COMMUNITY_URL)}
              className="w-full sm:w-auto px-6 py-3 rounded-md font-semibold text-white bg-gradient-to-r from-[#7c3aed] to-[#a855f7] disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              {waClicked ? "Joined" : "Join Community"}
            </button>
            <p className="text-xs text-[#8a7e5e] mt-2">(Opens in WhatsApp)</p>
          </Step>

          {/* STEP 4 - INSTAGRAM */}
          <Step n="4" color="#ec4899" icon={Instagram}
            title="Follow Us on Instagram"
            desc="Behind-the-scenes, updates, movies, events and vibes!">
            <button
              data-testid="wifi-instagram-btn"
              disabled={!leadId}
              onClick={() => trackClick("instagram", INSTAGRAM_URL)}
              className="w-full sm:w-auto px-6 py-3 rounded-md font-semibold text-white bg-gradient-to-r from-[#f59e0b] via-[#ec4899] to-[#8b5cf6] disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <Instagram className="w-5 h-5" />
              {igClicked ? "Followed" : "Follow @thecinevibeindia"}
            </button>
            <p className="text-xs text-[#8a7e5e] mt-2">(Opens in Instagram)</p>
          </Step>

          {/* STEP 5 - CONNECT */}
          <Step n="5" color="#56b6ff" icon={Wifi}
            title="You're All Set!"
            desc="Complete all steps above to enjoy FREE Wi-Fi.">
            <button
              data-testid="wifi-connect-btn"
              disabled={!allDone}
              onClick={connect}
              className="w-full px-6 py-4 rounded-md font-bold text-white bg-gradient-to-r from-[#2563eb] to-[#56b6ff] disabled:opacity-40 flex items-center justify-center gap-2 text-lg"
            >
              <Wifi className="w-6 h-6" /> CONNECT TO INTERNET
            </button>
          </Step>
        </div>

        <p className="text-center text-[#8a7e5e] text-xs mt-10">
          Lights. Camera. Chill. — This is your vibe.
        </p>
      </div>
    </div>
  );
}
